/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSearchIcon,
  deDE,
} from "@mui/x-data-grid";
import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Button,
  ImageListItem,
  ImageList,
  IconButton,
  Modal,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";

import { useDebounce } from "../../hooks/useDebounce";
import { getClinicPatients } from "../../api/clinic-patients";
import { Navbar } from "../styled/nav-bar";
import { PatientData, Program } from "../../interface/IPatinetDataClinicWeight";
import { UserProgramContext } from "../../context/user-program/user-program";
import useFileDownloader from "../../hooks/useFileDownloader";
import { useDocxViewer } from "../../hooks/useDocxViewer";
import { getfileUserEmployee } from "../../api/documents";

const clickableCellStyle = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  cursor: "pointer",
};

export const ClinicPatients: FC = () => {
  const [patients, setPatients] = useState<PatientData[]>([]);
  const [fileEmployee, setFileEmployee] = useState<any[]>([]);
  const [mmkProgram, setMMKProgram] = useState<Map<string, Array<unknown>>>();
  const { program } = useContext(UserProgramContext);

  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(
    searchParams.get("filter") || ("" as string)
  );

  const [pageState, setPageState] = useState({
    loading: true,
    currentPage: searchParams.get("page")
      ? Number(searchParams.get("page"))
      : 1,
    perPage: 10,
    totalPatients: 0,
    totalPages: 0,
  });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    handleDownloadClicked,
    handleDownloadClickedMmkBefund,
    handleDownloadTemporaryLink,
    setIsFileDetailSelected,
    setIsFileDetailSelectedClinic,
    isFileDetailSelectedClinic,
    isFileDetailSelected,
    temporaryUrl,
    isAPdfFile,
    isDocxFile,
  } = useFileDownloader();

  const docxContent = useDocxViewer(temporaryUrl);

  const handleDetailClose = () => {
    setIsFileDetailSelected(false);
    setIsFileDetailSelectedClinic(false);
  };

  const formatDate = (dateISOString: string) => {
    const date = new Date(dateISOString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const handleCellClick = (id: string) => {
    navigate(`/patients/${id}`);
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", flex: 0.3, hideable: true },
    {
      field: "firstName",
      headerName: "Vorname",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => {
        const { _id } = params.row.psychologicalReport.PatientData;

        return (
          <Box
            onClick={(event) => {
              event.stopPropagation();
              handleCellClick(_id);
            }}
            style={clickableCellStyle}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "lastName",
      headerName: "Nachname",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => {
        const { _id } = params.row.psychologicalReport.PatientData;

        return (
          <Box
            onClick={(event) => {
              event.stopPropagation();
              handleCellClick(_id);
            }}
            style={clickableCellStyle}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "phone",
      headerName: "Telefon",
      flex: 0.6,
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const { _id } = params.row.psychologicalReport.PatientData;

        return (
          <Box
            onClick={(event) => {
              event.stopPropagation();
              handleCellClick(_id);
            }}
            style={clickableCellStyle}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "E-Mail",
      flex: 0.8,
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const { _id } = params.row.psychologicalReport.PatientData;

        return (
          <Box
            onClick={(event) => {
              event.stopPropagation();
              handleCellClick(_id);
            }}
            style={clickableCellStyle}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "behavioralTraining",
      headerName: "Verhaltenstraining",
      flex: 0.5,
      hideable: true,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" flexWrap="wrap" mt={"25px"} mb={"25px"}>
          {Array.from({ length: params.value.completed || 0 }).map(
            (_, index) => (
              <CheckCircleRoundedIcon
                key={index}
                color="success"
                fontSize="small"
              />
            )
          )}
          {Array.from({
            length:
              Number(params.value.total) - Number(params.value.completed) || 0,
          }).map((_, index) => (
            <CircleOutlinedIcon key={`rest-${index}`} fontSize="small" />
          ))}
        </Box>
      ),
    },
    {
      field: "psychologicalReport",
      headerName: "Psychologisches Gutachten",
      flex: 0.6,
      hideable: true,
      sortable: false,
      filterable: false,

      renderCell: (params: GridRenderCellParams) => {
        const value = params.value;

        if (!fileEmployee || !Array.isArray(fileEmployee)) {
          console.error("fileEmployee is undefined or not an array");
          return null;
        }

        const findPatientWhitReport = fileEmployee.find(
          (file: any) => file?.id === value?.PatientData?._id
        );

        const urlFile =
          findPatientWhitReport?.fileData.find(
            (file: any) =>
              file.name === "Psychologisches Gutachten" && file.hidden === false
          )?.file || null;

        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            {urlFile && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  handleDownloadClicked({
                    url: urlFile,
                    hidden: false,
                    _id: "",
                    title: findPatientWhitReport?.name || "Unknown",
                    createdOn: new Date().toISOString(),
                    category: "Document Category",
                  });
                }}
              >
                Download
              </Button>
            )}

            <Modal open={isFileDetailSelected} onClose={handleDetailClose}>
              <Box
                sx={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "white",
                  border: "2px solid #000",
                  boxShadow: 24,
                  p: 4,
                  borderRadius: 5,
                  width: "900px",
                  height: "600px",
                }}
              >
                {isAPdfFile ? (
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <IconButton
                      onClick={() => handleDownloadTemporaryLink(temporaryUrl)}
                      sx={{ marginTop: "10px", padding: "10px" }}
                    >
                      <CloudDownloadIcon
                        sx={{ fontWeight: "bold", color: "black" }}
                        fontSize="medium"
                      />
                    </IconButton>
                  </Box>
                ) : null}

                {isAPdfFile ? (
                  <iframe
                    title="file"
                    src={temporaryUrl}
                    width="100%"
                    height="500px"
                  />
                ) : isDocxFile ? (
                  <Box
                    style={{
                      width: "100%",
                      height: "500px",
                      overflowY: "auto",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: docxContent || "Loading...",
                    }}
                  />
                ) : (
                  <ImageList
                    sx={{ width: "100%", height: 500 }}
                    cols={1}
                    rowHeight={450}
                  >
                    <ImageListItem>
                      <img
                        srcSet={temporaryUrl}
                        src={temporaryUrl}
                        alt={"File"}
                        loading="lazy"
                      />
                    </ImageListItem>
                  </ImageList>
                )}
              </Box>
            </Modal>
          </Box>
        );
      },
    },
    {
      field: "period",
      headerName: "MMK-Zeitraum",
      flex: 0.8,
      hideable: true,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {params.value ? (
            <Typography>{params.value}</Typography>
          ) : (
            <Button>
              <MoreHorizOutlinedIcon sx={{ color: "black" }} />
            </Button>
          )}
        </Box>
      ),
    },
    {
      field: "nutritionalTherapy",
      headerName: "Ernährungstherapie",
      flex: 0.7,
      hideable: true,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" flexWrap="wrap" mt={"25px"} mb={"25px"}>
          {Array.from({ length: params.value.completed || 0 }).map(
            (_, index) => (
              <CheckCircleRoundedIcon
                key={index}
                color="success"
                fontSize="small"
              />
            )
          )}
          {Array.from({
            length:
              Number(params.value.total) - Number(params.value.completed) || 0,
          }).map((_, index) => (
            <CircleOutlinedIcon key={`rest-${index}`} fontSize="small" />
          ))}
        </Box>
      ),
    },
    {
      field: "sport",
      headerName: "Sport",
      flex: 0.5,
      hideable: true,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" flexWrap="wrap" mt={"25px"} mb={"25px"}>
          {Array.from({ length: params.value.completed || 0 }).map(
            (_, index) => (
              <CheckCircleRoundedIcon
                key={index}
                color="success"
                fontSize="small"
              />
            )
          )}
          {Array.from({
            length:
              Number(params.value.total) - Number(params.value.completed) || 0,
          }).map((_, index) => (
            <CircleOutlinedIcon key={`rest-${index}`} fontSize="small" />
          ))}
        </Box>
      ),
    },
    {
      field: "findings",
      headerName: "MMK-Befund",
      flex: 0.5,
      hideable: true,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value;

        if (!fileEmployee || !Array.isArray(fileEmployee)) {
          console.error("fileEmployee is undefined or not an array");
          return null;
        }

        const findPatientWhitReport = fileEmployee.find(
          (file: any) => file?.id === value?.PatientData?._id
        );

        const urlFile =
          findPatientWhitReport?.fileData.find(
            (file: any) => file.name === "MMK-Befund" && file.hidden === false
          )?.file || null;

        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            {urlFile && (
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleDownloadClickedMmkBefund({
                      url: urlFile,
                      hidden: false,
                      _id: "",
                      title: findPatientWhitReport?.name || "Unknown",
                      createdOn: new Date().toISOString(),
                      category: "Document Category",
                    });
                  }}
                >
                  Download
                </Button>
              </Box>
            )}

            <Modal
              open={isFileDetailSelectedClinic}
              onClose={handleDetailClose}
            >
              <Box
                sx={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "white",
                  border: "2px solid #000",
                  boxShadow: 24,
                  p: 4,
                  borderRadius: 5,
                  width: "900px",
                  height: "600px",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton
                    onClick={() => handleDownloadTemporaryLink(temporaryUrl)}
                    sx={{ marginTop: "10px", padding: "10px" }}
                  >
                    <CloudDownloadIcon
                      sx={{ fontWeight: "bold", color: "black" }}
                      fontSize="medium"
                    />
                  </IconButton>
                </Box>
                {isAPdfFile ? (
                  <iframe
                    title="file"
                    src={temporaryUrl}
                    width="100%"
                    height="500px"
                  />
                ) : isDocxFile ? (
                  <Box
                    style={{
                      width: "100%",
                      height: "500px",
                      overflowY: "auto",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: docxContent || "Loading...",
                    }}
                  />
                ) : (
                  <ImageList
                    sx={{ width: "100%", height: 500 }}
                    cols={1}
                    rowHeight={450}
                  >
                    <ImageListItem>
                      <img
                        srcSet={temporaryUrl}
                        src={temporaryUrl}
                        alt={"File"}
                        loading="lazy"
                      />
                    </ImageListItem>
                  </ImageList>
                )}
              </Box>
            </Modal>
          </Box>
        );
      },
    },
  ];

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const getColumns = () => {
    if (isMobile) {
      return columns.map((column) => ({ ...column, flex: 0, wirth: 150 }));
    } else {
      return columns;
    }
  };

  const debounceValue = useDebounce(searchValue, 500);

  const formatMMKProgram = (mmkProgram: Program) => {
    const mmkProgramMap = new Map<string, Array<unknown>>();
    mmkProgram?.sections
      ?.map(({ title, steps }) => ({
        title,
        steps: steps.map(({ _id }) => ({
          _id,
        })),
      }))
      .forEach((section) => {
        mmkProgramMap.set(section.title, section.steps);
      });
    return mmkProgramMap;
  };

  const getCountOfSteps = (
    sectionTitle: string,
    userProgramStepProgress: PatientData["userProgram"]["stepsProgress"]
  ) => {
    const steps = mmkProgram?.get(sectionTitle) as { _id: string }[];
    return (
      steps?.filter(
        (step) =>
          userProgramStepProgress?.some(
            (userProgramStep) => userProgramStep.stepId === step._id
          ) ?? false
      ).length ?? 0
    );
  };

  const clinicParam =
    program?.clinicCode && program.clinicCode.length > 0
      ? program.clinicCode
      : "PWS-001";

  useEffect(() => {
    const fetchData = async () => {
      setPageState((prevPageState) => ({ ...prevPageState, loading: true }));
      try {
        const data = await getClinicPatients({
          clinicCode: clinicParam,
          perPage: pageState.perPage,
          currentPage: pageState.currentPage,
          searchValue: debounceValue,
        });

        setPatients(data.patients);
        setMMKProgram(formatMMKProgram(data.mmkProgram));

        setPageState((prevPageState) => ({
          ...prevPageState,
          totalPatients: data.totalPatients,
          totalPages: data.totalPages,
          loading: false,
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [pageState.perPage, pageState.currentPage, debounceValue]);

  useEffect(() => {
    if (patients.length > 0) {
      const getFilesForAllPatients = async () => {
        try {
          const files = await Promise.all(
            patients.map(async (patient) => {
              const response = await getfileUserEmployee(patient.user._id);
              if (response.files.length > 0 && response.files !== undefined) {
                return {
                  id: patient.user._id,
                  fileData: response.files,
                };
              }
            })
          );

          setFileEmployee(files.filter(Boolean));
        } catch (error) {
          console.error("Error fetching employee files:", error);
        }
      };

      getFilesForAllPatients();
    }
  }, [patients]);

  const mapPatientsToRows = () =>
    patients.map(
      ({ _id, user: { firstName, lastName, email, phone }, userProgram }) => ({
        id: _id,
        firstName,
        lastName,
        email,
        phone,
        behavioralTraining: {
          total: mmkProgram?.get("Verhaltenstraining")?.length ?? 0,
          completed: getCountOfSteps(
            "Verhaltenstraining",
            userProgram?.stepsProgress
          ),
        },
        psychologicalReport: {
          PatientData: patients.find((patient) => patient._id === _id)?.user,
        },
        period: userProgram
          ? `${formatDate(userProgram.startDate)} - ${formatDate(
              userProgram.endDate
            )}`
          : "",
        nutritionalTherapy: {
          total: mmkProgram?.get("Ernährung")?.length ?? 0,
          completed: getCountOfSteps("Ernährung", userProgram?.stepsProgress),
        },
        sport: {
          total: mmkProgram?.get("Sport")?.length ?? 0,
          completed: getCountOfSteps("Sport", userProgram?.stepsProgress),
        },
        findings: {
          PatientData: patients.find((patient) => patient._id === _id)?.user,
        },
      })
    );

  const rows = mapPatientsToRows();

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      spacing={4}
      pt={2}
      pr={2}
      pl={2}
      pb={5}
    >
      <Navbar />

      <Box
        sx={{
          marginTop: "2em",
          marginRight: "2em",
          marginLeft: "2em",
          width: "95%",
        }}
      >
        <Grid container spacing={4} p={3}>
          <Grid item xs={12} sm={7}>
            <Typography variant="h5" fontWeight="bold">
              Patientenliste
            </Typography>
          </Grid>

          <Grid item xs={12} sm={5} justifyContent="end">
            <TextField
              fullWidth
              value={searchValue}
              label="Suche"
              variant="standard"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GridSearchIcon />
                  </InputAdornment>
                ),
              }}
              onInput={handleInputChange}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              width: "100%",

              overflowX: "auto",
            }}
          >
            <DataGrid
              autoHeight
              rows={rows}
              columns={getColumns()}
              rowCount={pageState.totalPatients}
              loading={pageState.loading}
              rowsPerPageOptions={[5, 10, 15]}
              pagination
              page={pageState.currentPage - 1}
              pageSize={pageState.perPage}
              paginationMode="server"
              onPageChange={(newPage) =>
                setPageState({ ...pageState, currentPage: newPage + 1 })
              }
              onPageSizeChange={(newPageSize) =>
                setPageState({ ...pageState, perPage: newPageSize })
              }
              checkboxSelection={false}
              columnVisibilityModel={{
                id: false,
              }}
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              sx={{
                minWidth: 1800,
                width: "120%",
                ".MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                ".MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};
